import * as z from 'zod';

import type {BrandTagFragment} from '@/modules/commerce/brand/query';
import type {ProductFilters} from '@/modules/commerce/filters/query';
import type {ItemForPriceFragment} from '@/modules/commerce/item/query';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import type {ProductAdapter} from '../../adapter';
import type {ProductDescription} from '../../description';
import {DOCUMENT_TYPE_FONT} from '../../font/constants';
import {type ProductImageSide} from '../../ProductImage';
import type {Overlay} from '../../ProductImageStack';
import {
	getDefaultOverlayPlacement,
	type ProductImageStackDescription,
} from '../../ProductImageStack';
import type {UnionProductCoreFragment} from '../../query';
import type {StickerProductCoreFragment, StickerProductFragment} from '../query';
import {DOCUMENT_TYPE_STICKER_PRODUCT} from '../schema/names';

import {
	getDefaultImageOverlayForStickerElement,
	getDefaultStickerElements,
	getImageOverlayForStickerElement,
	getSelectableStickerElements,
	getSelectedStickerElement,
	getStickerElementsAppropriateForProductFilters,
	getStickerType,
	hasStickerElementConfigs,
	isStickerFragPrintElement,
	isStickerPrintElementConfig,
	isStickerTextElementConfig,
	isValidStickerElementConfig,
	parseStickerElementsFromData,
	stickerElementConfigSchema,
} from './stickerElementConfiguration';
import {
	getStickerLocationName,
	parseStickerLocation,
	STICKER_LOCATION_OUTSIDE,
	type StickerLocation,
} from './stickerLocationConfiguration';

/*----------------------------------- KEYS & ENCODING -----------------------------------*/

export const STICKER_COLOR_KEY = 'colorId';
export const STICKER_VARIANT_KEY = 'variantId';

export const STICKER_PROPERTY_KEYS = [STICKER_COLOR_KEY, STICKER_VARIANT_KEY] as const;

export type StickerKey = (typeof STICKER_PROPERTY_KEYS)[number];

function parseStickerProperty(data: FormData | URLSearchParams, key: StickerKey) {
	return data.get(key)?.toString() ?? null;
}

/*----------------------------------- PRODUCT CONFIGURATION -----------------------------------*/

export const stickerConfigurationSchema = z.object({
	variantId: z.string().nullable(),
	colorId: z.string().nullable(),
	location: z.custom<StickerLocation>().nullable(),
	elements: z.array(stickerElementConfigSchema),
});

export type StickerProductConfiguration = z.infer<typeof stickerConfigurationSchema>;

export type StickerProductValidationResult = {
	isValid: boolean;
	errors: {
		variant: string | null;
		element: string | null;
	};
};

function getSelectedStickerColor(
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration | null,
) {
	if (!fragment.colors || fragment.colors.length === 0) {
		return null;
	}
	return (
		fragment.colors.find((color) => color._id === config?.colorId) ??
		getDefaultStickerColor(fragment)
	);
}

function getSelectedStickerColorHex(
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration | null,
) {
	return getSelectedStickerColor(fragment, config)?.hex ?? '#FFFFFF';
}

function getDefaultStickerColor(fragment: StickerProductCoreFragment) {
	return getStickerType(fragment) === 'singleColor' ? fragment?.colors?.at(0) : null;
}

function getDefaultStickerColorId(fragment: StickerProductCoreFragment) {
	return getDefaultStickerColor(fragment)?._id ?? null;
}

function getSelectedStickerVariant(
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration | null,
) {
	return config
		? fragment.variants?.find((v) => v._key === config.variantId)
		: getDefaultStickerVariant(fragment);
}

function getBrandIdsFromStickerFragment(fragment: StickerProductCoreFragment) {
	const brandIds = new Set<string>();

	for (const brand of fragment.brands ?? []) {
		brandIds.add(brand._id);
	}

	const selectable = getSelectableStickerElements(fragment);

	for (const element of selectable) {
		if (!isStickerFragPrintElement(element)) {
			continue;
		}

		for (const {brands} of element?.collection?.prints ?? []) {
			for (const brand of brands ?? []) {
				brandIds.add(brand._id);
			}
		}
	}

	return brandIds;
}

function getDefaultStickerVariant(fragment: StickerProductCoreFragment) {
	return fragment.variants && fragment.variants.length > 0 ? fragment.variants[0] : null;
}

function getDefaultStickerVariantId(fragment: StickerProductCoreFragment): string | null {
	return getDefaultStickerVariant(fragment)?._key ?? null;
}

export function isStickerProduct<P extends UnionProductCoreFragment>(
	fragment: P,
): fragment is Extract<P, {_type: typeof DOCUMENT_TYPE_STICKER_PRODUCT}> {
	return fragment._type === DOCUMENT_TYPE_STICKER_PRODUCT;
}

export function isValidStickerConfiguration(
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration,
) {
	return validateStickerConfiguration(fragment, config).isValid;
}

export function validateStickerConfiguration(
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration,
): StickerProductValidationResult {
	const result: StickerProductValidationResult = {
		isValid: true,
		errors: {
			variant: null,
			element: null,
		},
	};

	// Validate that a valid variant has been selected:
	const variant = fragment.variants?.find((v) => v._key === config.variantId);

	if (!variant) {
		result.errors.variant = 'Du må velge en variant.';
	}

	// FIXME: Validate options like color and mounting side being selected if the product has those options available

	// Validate that all selectable elements have a valid config:
	const selectable = getSelectableStickerElements(fragment);

	for (const element of selectable) {
		const elementConfig = config.elements.find((e) => e.id === element._key);
		if (!elementConfig) {
			result.errors.element = 'En eller flere av de valgte elementene er ugyldige.';
			continue;
		}

		if (!isValidStickerElementConfig(elementConfig, fragment)) {
			result.errors.element = 'En eller flere av de valgte elementene er ugyldige.';
			continue;
		}
	}

	result.isValid = !result.errors.variant && !result.errors.element;

	return result;
}

export function getItemsForStickerConfiguration(
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration,
): ItemForPriceFragment[] {
	const variant = getSelectedStickerVariant(fragment, config);
	const items: ItemForPriceFragment[] = [];

	if (variant?.items?.length) {
		items.push(...variant.items);
	}

	return items;
}

export function getProductNameForStickerConfiguration(
	fragment: StickerProductCoreFragment,
	_config: StickerProductConfiguration,
): string {
	return `${fragment.name}`;
}

export function getImageStackForStickerConfiguration<Side extends ProductImageSide>(
	side: Side,
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration | null,
	allowPlaceholders: boolean,
): ProductImageStackDescription | null {
	if (side !== 'front') {
		return null;
	}

	const baseImage = imageFromSanityImage(fragment.baseImage);

	if (!baseImage) {
		return null;
	}

	const altText = fragment?.baseImage?.altText ?? '';

	const overlays: Overlay[] = [];

	// use the same color hex across all elements
	const colorHex = getSelectedStickerColorHex(fragment, config);

	const stickerType = getStickerType(fragment);
	const selectable = getSelectableStickerElements(fragment);

	// push monochrome sticker base image as the first layer to recolor the base image
	if (stickerType === 'singleColor') {
		overlays.push({
			_type: 'maskedColorOverlay',
			placement: getDefaultOverlayPlacement(),
			maskImage: baseImage,
			colorHex,
		});
	}

	const configElements = config?.elements;

	if (hasStickerElementConfigs(configElements)) {
		for (const element of configElements) {
			const imageStack = getImageOverlayForStickerElement(element, selectable, colorHex);

			if (!imageStack) {
				continue;
			}

			overlays.push(imageStack);
		}
	} else if (allowPlaceholders) {
		for (const element of selectable) {
			const imageStack = getDefaultImageOverlayForStickerElement(element, colorHex);

			if (!imageStack) {
				continue;
			}

			overlays.push(imageStack);
		}
	}

	return {
		image: baseImage,
		altText,
		overlays,
	};
}

export function getDescriptionForStickerConfiguration(
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration,
): ProductDescription {
	const desc: ProductDescription = [];

	const variant = getSelectedStickerVariant(fragment, config);

	if (variant) {
		desc.push({
			title: 'Variant',
			value: variant.name ?? variant.items?.at(0)?.name ?? 'Uten navn',
		});
	}

	desc.push({
		title: 'Type',
		value: fragment.stickerType === 'multiColor' ? 'Flerfarget' : 'Enfarget',
	});

	if (config.colorId) {
		const color = getSelectedStickerColor(fragment, config);
		desc.push({
			title: 'Farge',
			value: [
				{
					title: 'Fargenavn ' + '(' + (color?.hex ?? 'Uten hekskode') + ')',
					value: color?.name ?? 'Uten navn',
				},
			],
		});
	}

	desc.push({
		title: 'Monteringsside',
		value: getStickerLocationName(config.location ?? 'outside'),
	});

	if (hasStickerElementConfigs(config.elements)) {
		config.elements.forEach((element) => {
			const result = isValidStickerElementConfig(element, fragment);

			if (!result || !result.name) {
				return;
			}

			const {selected} = result;

			if (!selected || !selected.name) {
				return;
			}

			if (selected._type === DOCUMENT_TYPE_FONT && isStickerTextElementConfig(element)) {
				desc.push({
					title: result.name,
					value: [
						{title: 'Font', value: selected.name ?? 'Font uten navn'},
						{title: 'Tekst', value: element.text ?? ''},
					],
				});
			} else {
				desc.push({
					title: result.name,
					value: [{title: 'Trykk', value: selected.name ?? 'Trykk uten navn'}],
				});
			}
		});
	}

	return desc;
}

export function getStickerBrandsFromConfiguration(
	fragment: StickerProductCoreFragment,
	config: StickerProductConfiguration,
): BrandTagFragment[] {
	const brands = new Map<string, BrandTagFragment>(
		fragment.brands?.map((brand) => [brand._id, brand]),
	);

	const selectable = getSelectableStickerElements(fragment);

	for (const element of config.elements) {
		const result = getSelectedStickerElement(element, selectable);

		if (!result) {
			continue;
		}

		const {selected} = result;

		if (selected?._type === 'singleColorPrint' || selected?._type === 'multiColorPrint') {
			for (const brand of selected?.brands ?? []) {
				if (!brands.has(brand._id)) {
					brands.set(brand._id, brand);
				}
			}
		}
	}

	return Array.from(brands.values());
}

export function getFiltersMatchingStickerProduct(
	fragment: StickerProductCoreFragment,
	filters: ProductFilters,
): ProductFilters | null {
	// return null ONLY if there is filters and no matches were found, if there are filters, return product brands only

	if (!filters.brandIds || filters.brandIds.length === 0) {
		return {
			brandIds: fragment.brands?.map((brand) => brand._id),
		} satisfies ProductFilters;
	}

	const brandIds = getBrandIdsFromStickerFragment(fragment);

	if (brandIds.size === 0) {
		return null;
	}

	const filtered = filters.brandIds.filter((brandId) => brandIds.has(brandId));

	if (filtered.length === 0) {
		return null;
	}

	return {
		brandIds: filtered,
	} satisfies ProductFilters;
}

export function getDefaultStickerConfiguration(
	fragment: StickerProductCoreFragment,
): StickerProductConfiguration {
	const config: StickerProductConfiguration = {
		variantId: getDefaultStickerVariantId(fragment),
		colorId: getDefaultStickerColorId(fragment),
		location: fragment.canSelectMountingSide ? STICKER_LOCATION_OUTSIDE : null,
		elements: getDefaultStickerElements(fragment),
	};

	return config;
}

export function getNonOpionionatedDefaultStickerConfiguration(
	fragment: StickerProductCoreFragment,
): StickerProductConfiguration {
	const config: StickerProductConfiguration = {
		variantId: getDefaultStickerVariantId(fragment),
		colorId: getDefaultStickerColorId(fragment),
		location: fragment.canSelectMountingSide ? STICKER_LOCATION_OUTSIDE : null,
		elements: getDefaultStickerElements(fragment).filter((elementConfig) => {
			// We don't want to pre-select a branded element. A consequence of doing that could be
			// Calwin urinating on a random brand logo, which we don't want.
			//
			// By filtering out these element configs here, the image preview will fall back to using
			// the standard image for this element.
			if (isStickerPrintElementConfig(elementConfig)) {
				return false;
			}

			return true;
		}),
	};

	return config;
}

export function getNonOpinionatedStickerConfigurationAppropriateForFilters(
	fragment: StickerProductCoreFragment,
	filters?: ProductFilters,
) {
	const config = getNonOpionionatedDefaultStickerConfiguration(fragment);

	if (!filters) {
		return config;
	}

	const colors = fragment.colors ?? [];

	// match sticker color, shared color across all composite elements
	const filterColor = filters.colorIds?.find((id) => colors.find((color) => color._id === id));

	if (filterColor) {
		config.colorId = filterColor;
	}

	const filtered = getStickerElementsAppropriateForProductFilters(fragment, filters);

	config.elements = filtered.length > 0 ? filtered : config.elements;

	return config;
}

export function getStickerConfigurationAppropriateForFilters(
	fragment: StickerProductCoreFragment,
	filters?: ProductFilters,
) {
	const config = getDefaultStickerConfiguration(fragment);

	if (!filters) {
		return config;
	}

	const colors = fragment.colors ?? [];

	// match sticker color, shared color across all composite elements
	const filterColor = filters.colorIds?.find((id) => colors.find((color) => color._id === id));

	if (filterColor) {
		config.colorId = filterColor;
	}

	const filtered = getStickerElementsAppropriateForProductFilters(fragment, filters);

	config.elements = filtered.length > 0 ? filtered : config.elements;

	return config;
}

export function parseStickerConfigurationFromData(
	fragment: StickerProductCoreFragment,
	data: FormData,
): StickerProductConfiguration | null {
	const config = getDefaultStickerConfiguration(fragment);

	config.variantId = parseStickerProperty(data, STICKER_VARIANT_KEY);
	config.colorId = parseStickerProperty(data, STICKER_COLOR_KEY);
	config.location = parseStickerLocation(data);
	config.elements = parseStickerElementsFromData(fragment, data);

	return config;
}

export function parseStickerConfiguration(json: unknown): StickerProductConfiguration | null {
	try {
		const parsed = stickerConfigurationSchema.parse(json);
		return parsed;
	} catch (error) {
		console.error('Could not parse sticker configuration from json data', {
			json,
			error,
		});
		return null;
	}
}

export const stickerProductAdapter: ProductAdapter<
	StickerProductFragment,
	StickerProductCoreFragment
> = {
	getName: getProductNameForStickerConfiguration,
	getItems: getItemsForStickerConfiguration,
	getImages: getImageStackForStickerConfiguration,
	getDescription: getDescriptionForStickerConfiguration,
	isValidConfig: isValidStickerConfiguration,
	getMatchingFilters: getFiltersMatchingStickerProduct,
	getDefaultConfig: getDefaultStickerConfiguration,
	parseConfigFromJSON: parseStickerConfiguration,
	parseConfigFromFormData: parseStickerConfigurationFromData,
};
